import { useCallback, useEffect, useRef } from "react";

/**
 * Wheel/knob specialty picker. As the user scrolls, items near the vertical
 * center are large & opaque; items further away shrink and fade out.
 */
export default function SpecialtyWheel({ items, onSelect }) {
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  const update = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const els = c.querySelectorAll("[data-wheel-item]");
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      const center = r.top + r.height / 2;
      const dist = Math.abs(center - mid);
      const norm = Math.min(dist / (rect.height / 2), 1);
      el.style.opacity = String(Math.max(0.85, 1 - norm * 0.3));
      el.style.transform = `scale(${1 - norm * 0.2})`;
      el.style.fontWeight = "400";
    });
  }, []);

  const onScroll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(update);
  }, [update]);

  useEffect(() => {
    const t = setTimeout(update, 60);
    return () => clearTimeout(t);
  }, [items, update]);

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="no-scrollbar relative overflow-y-auto pb-[38%] pt-[6%]"
      style={{ height: "100%" }}
      data-testid="specialty-wheel"
    >
      {items.map((sp) => (
        <button
          key={sp}
          data-wheel-item
          data-testid={`specialty-${sp}`}
          onClick={(e) => {
            const el = e.currentTarget;
            el.animate(
              [
                { transform: "scale(1)" },
                { transform: "scale(0.86)" },
                { transform: "scale(1.12)" },
                { transform: "scale(1)" },
              ],
              { duration: 280, easing: "ease-out" }
            );
            setTimeout(() => onSelect(sp), 240);
          }}
          className="mx-auto block w-full py-3 text-center text-2xl leading-tight text-white"
        >
          {sp}
        </button>
      ))}
    </div>
  );
}
